import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/PageHeader";
import { StaleDataMarker } from "@/components/ConnectionBadge";
import { RunsTable } from "@/components/fleet/RunsTable";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import { ApiError, get, qs } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { S, runStatusLabel } from "@/lib/strings";
import {
  DATE_PRESETS,
  qsFromRunFilter,
  runFilterFromParams,
  type DatePreset,
  type RunsUrlFilter,
} from "@/lib/runFilters";
import type { DeviceSummary, Page, RunStatus, TaskRow } from "@/types/fleet";

const STATUSES: RunStatus[] = [
  "QUEUED",
  "WAITING_UNLOCK",
  "RUNNING",
  "SUCCESS",
  "FAILED",
  "SKIPPED",
  "CANCELLED",
];

export function Runs() {
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => runFilterFromParams(params), [params]);

  const patch = useCallback(
    (next: Partial<RunsUrlFilter>) => {
      const merged = { ...filter, ...next };
      setParams(new URLSearchParams(qsFromRunFilter(merged).replace(/^\?/, "")), { replace: true });
    },
    [filter, setParams],
  );

  // Filter dropdowns need names, not ids. Both lists are small and cached.
  const devices = useQuery<Page<DeviceSummary>, ApiError>({
    queryKey: ["devices", "filter-options"],
    queryFn: () => get<Page<DeviceSummary>>(`/devices${qs({ limit: 200 })}`),
    staleTime: 5 * 60_000,
  });

  const tasks = useQuery<Page<TaskRow>, ApiError>({
    queryKey: qk.tasks(),
    queryFn: () => get<Page<TaskRow>>("/tasks"),
    staleTime: 5 * 60_000,
  });

  const hasFilters =
    !!filter.device_id ||
    !!filter.task_id ||
    !!filter.status ||
    !!filter.dispatch_id ||
    !!filter.q ||
    filter.has_error === true ||
    (!!filter.date && filter.date !== "all");

  return (
    <>
      <PageHeader
        title={S.runsTitle}
        hint={
          <>
            {S.runsHint}
            <StaleDataMarker className="ml-1" />
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select
          size="sm"
          aria-label={S.filterDevice}
          value={filter.device_id ?? ""}
          onChange={(e) => patch({ device_id: e.target.value || undefined })}
          placeholder={S.filterDevice}
          options={(devices.data?.items ?? []).map((d) => ({ value: d.id, label: d.label }))}
        />

        <Select
          size="sm"
          aria-label={S.filterTask}
          value={filter.task_id ?? ""}
          onChange={(e) => patch({ task_id: e.target.value || undefined })}
          placeholder={S.filterTask}
          options={(tasks.data?.items ?? []).map((t) => ({ value: t.id, label: t.name }))}
        />

        <Select
          size="sm"
          aria-label={S.filterStatus}
          value={filter.status ?? ""}
          onChange={(e) => patch({ status: (e.target.value || undefined) as RunStatus })}
          placeholder={S.filterStatus}
          options={STATUSES.map((s) => ({ value: s, label: runStatusLabel(s) }))}
        />

        <Select
          size="sm"
          aria-label={S.filterDate}
          value={filter.date ?? "all"}
          onChange={(e) => patch({ date: e.target.value as DatePreset })}
          options={DATE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
        />

        <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-[var(--color-fg-subtle)]">
          <Switch
            checked={filter.has_error === true}
            onCheckedChange={(v) => patch({ has_error: v || undefined })}
          />
          {S.filterHasError}
        </label>

        {filter.q && (
          <span className="inline-flex items-center gap-1 rounded bg-[rgba(255,255,255,0.06)] px-2 py-0.5 font-mono text-[11px]">
            error ~ {filter.q}
            <button
              type="button"
              onClick={() => patch({ q: undefined })}
              className="text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
              aria-label={S.clear}
            >
              ×
            </button>
          </span>
        )}

        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={() => setParams(new URLSearchParams())}>
            {S.clearFilters}
          </Button>
        )}
      </div>

      <RunsTable filter={filter} />
    </>
  );
}
